import React from "react";
import ReceiptHeader from "../../components/ReceiptHeader";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import QRCode from "react-qr-code";
import is_money from "../../helpers/is_money";
import is_null from "../../helpers/is_null";
import { appHost } from "../../helpers/uri";

const AcceptanceReceipt = () => {
  const { id } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["acceptanceReceiptInfo"],
    queryFn: async () => {
      const req = await axiosget(`ms_acceptance_fee_rrr/receipt/${id}`);
      return req?.data;
    },
  });
  if (!is_null(data)) {
    setTimeout(() => window.print(), 500);
  }
  return (
    <>
      {isLoading && <Loader />}
      {error && <Alert variant="danger"><AlertDescription>{`Local server error: ${error}`}</AlertDescription></Alert>}
      {data?.message !== undefined && <Alert variant="danger"><AlertDescription>{data?.message}</AlertDescription></Alert>}
      {data?.message === undefined && (
        <div
          id="printDocument"
          style={{
            display: "block",
            backgroundImage: data?.acceptanceFee?.status ? `url('${appHost}/images/paid.png')` : `url('${appHost}/images/not-paid.png')`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center 250px",
            backgroundSize: "400px 400px",
            minHeight: "100vh",
            padding: "20px"
          }}
        >
          <ReceiptHeader office={`PAYMENT RECEIPT Bursary Department`} />
          <div className="container mt-5">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">
              <div className="row">
                {/* Receipt Details */}
                <div className="col-md-8">
                  <div className="space-y-4">
                    <div className="flex flex-col mb-4">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Candidate Name</span>
                      <span className="text-lg font-bold text-gray-900 uppercase">
                        {data?.applicant?.firstname} {data?.applicant?.middlename} {data?.applicant?.lastname}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Admission Code</span>
                        <div className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-1">
                          {data?.code?.replace("ATBCOE/", "")}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Date & Time</span>
                        <div className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-1">
                          {new Date(data?.acceptanceFee?.createdAt || data?.createdAt).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Phone Number</span>
                        <div className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-1">
                          {data?.applicant?.phonenumber}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Email Address</span>
                        <div className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-1">
                          {data?.applicant?.emailaddress}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Programme</span>
                      <div className="text-sm font-semibold text-gray-900 leading-tight">
                        {data?.programme?.code} - {data?.programme?.name}
                      </div>
                    </div>

                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Transaction Status</span>
                          <div className={`text-sm font-bold ${data?.acceptanceFee?.status ? "text-green-600" : "text-red-600"}`}>
                            {data?.acceptanceFee?.status ? "✅ Confirmed Paid" : "❌ Pending Payment"}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Amount Paid</span>
                          <div className="text-2xl font-black text-gray-900">
                            {is_money(data?.acceptanceFee?.details?.amount || data?.acceptanceFee?.paid_details?.amount || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* QR Code & RRR */}
                <div className="col-md-4 flex flex-col items-center justify-center border-l border-gray-100 pl-4">
                  <div className="p-2 border-4 border-gray-900 rounded-lg bg-white mb-4">
                    <QRCode
                      size={180}
                      value={btoa(
                        JSON.stringify({
                          rrr: data?.acceptanceFee?.RRR,
                          name: `${data?.applicant?.firstname} ${data?.applicant?.lastname}`,
                          code: data?.code,
                          amount: data?.acceptanceFee?.details?.amount
                        })
                      )}
                    />
                  </div>
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">REMITA RRR</span>
                  <div className="text-2xl font-black tracking-widest text-gray-900 bg-gray-100 px-4 py-1 rounded">
                    {data?.acceptanceFee?.RRR}
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-dashed border-gray-300 text-center">
                <p className="text-xs text-gray-400 italic">
                  This receipt is electronically generated. For any inquiries, please contact the Bursary department with your RRR.
                </p>
                {data?.acceptanceFee?.details?.description && (
                  <p className="text-sm font-bold text-gray-600 mt-1 uppercase">
                    {data?.acceptanceFee?.details?.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AcceptanceReceipt;
